import {
  Box,
  Card,
  CardContent,
} from "@material-ui/core";
import {GradientButton} from "../../../property-page-components/PropertyForm";
import React, {FC, useEffect, useState} from "react";
import {useFormik} from "formik";
import * as Yup from "yup";
import {retrieveCompaniesOptions} from "../../../../../services/companyService";
import {useSelector} from "../../../../../redux/store";
import {getUser} from "../../../../../selectors";
import CompanyInformationSection from "./sections/CompanyInformationSection";
import CompanyAddressSection from "./sections/CompanyAddressSection";
import CompanySupportsSection from "./sections/CompanySupportsSection";
import CompanyContractsSection from "./sections/CompanyContractsSection";
import CompanyButtonSettingsSection from "./sections/CompanyButtonSettingsSection";
import {fetchProducts, retrieveContractOptions} from "../../../../../services/hubspotContractService";
import {CircularProgress} from "@mui/material";
import * as types from "../../../../../constants";
import {useDispatch} from "react-redux";
import {LANGUAGE_INFO_MESSAGE} from "../../../../../constants/values";
import CompanyAPIKeySection from "./sections/CompanyAPIKeySection";

const contractValidationSchema = Yup.object().shape({
  amount: Yup.number().typeError('The amount is required').required('The amount is required'),
  deal_id: Yup.number().typeError('Deal ID is required').required('Deal ID is required'),
  deal_name: Yup.string(),
  contract_name: Yup.string(),
  deal_type: Yup.string(),
  annual_contract_value: Yup.number().required('Annual contract value is required').typeError('Annual contract value is required'),
  total_contract_value: Yup.number(),
  date_of_contract_signing: Yup.date(),
  billing_period: Yup.string(),
  subscription_period: Yup.date(),
  invoice_status: Yup.string(),
  products: Yup.array(),
});

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Name is required'),
  email: Yup.string().email().required('Email is required'),
  registration_number: Yup.number().required('Registration number is required').typeError('The registration number must be a number'),
  nr_employees: Yup.string().required('Nr employers is required'),
  supported_a11y_standards: Yup.array().min(1, 'Supported standards is required').required('Supported standards is required'),
  address: Yup.object({
    address_line_1: Yup.string().required('Address is required'),
  }),
  hubspot_company_id: Yup.number().required('Hubspot company id is required').typeError('The hubspot company id must be a number'),
  company_api_key: Yup.object({
    name: Yup.string().required('Company API name is required'),
    expiry_date: Yup.date().required('Expires date is required')
  })
  // contracts: Yup.array().of(contractValidationSchema)
});

const INITIAL_VALUES_ADDRESS = {
    address_line_1: '',
    city: '',
    country: '',
    floor: '',
    office_number: '',
    postcode: '',
    region_code: ''
}

const INITIAL_VALUES_COMPANY_API_KEY = {
  name: '',
  key: '',
  expiry_date: '',
  revoked: false,
  prefix: '',
}

const INITIAL_VALUES = {
  name: '',
  email: '',
  registration_number: '',
  nr_employees: '',
  hubspot_company_id: '',
  type: '',
  logo: '',
  address: INITIAL_VALUES_ADDRESS,
  contracts: [],
  supported_a11y_standards: [],
  supported_languages: [],
  company_api_key: INITIAL_VALUES_COMPANY_API_KEY
}

type Option = {
  value: string,
  display_name: string
}

type PropsType = {
  initialValues?: any
  onSubmit: (values: any) => void
  loading?: boolean
}

const CompanyForm: FC<PropsType> = ({
  initialValues = INITIAL_VALUES,
  onSubmit,
  loading = false,
}) => {
  const dispatch = useDispatch()
  const user = useSelector(getUser);
  const [a11yStandardsOptions, setA11yStandardsOptions] = useState<Option[]>([])
  const [languagesOptions, setLanguagesOptions] = useState<Option[]>([])
  const [typeOptions, setTypeOptions] = useState<Option[]>([])
  const [employeesOptions, setEmployeesOptions] = useState<Option[]>([])
  const [invoiceStatusOptions, setInvoiceStatusOptions] = useState<Option[]>([])
  const [products, setProducts] = useState<any>([])
  const [editMode, setEditMode] = useState(false)

  useEffect(() => {
    if (initialValues.id) {
      setEditMode(true)
    }
  }, [initialValues]);

  useEffect(() => {
    retrieveCompaniesOptions(user!.token).then((response: any) => {
      if (response.actions.POST) {
        const post = response.actions.POST
        setA11yStandardsOptions(post.supported_a11y_standards.child.choices)
        setLanguagesOptions(post.supported_languages.child.choices)
        setTypeOptions(post.type.choices)
        setEmployeesOptions(post.nr_employees.choices)
      }
    })
    if (invoiceStatusOptions.length === 0) {
      retrieveContractOptions(user!.token).then((response: any) => {
        if (response.actions.POST) {
          const post = response.actions.POST
          setInvoiceStatusOptions(post.invoice_status.choices)
        }
      })
    }
    // TODO uncomment when we will have contract logic
    // if (products.length === 0) {
    //   fetchProducts(user!.token).then((results) => {
    //     setProducts(results)
    //   })
    // }
  }, []);

  const {
    handleSubmit,
    handleChange,
    setFieldValue,
    setValues,
    values,
    errors,
    touched
  } = useFormik({
    initialValues: {
      ...initialValues,
      address: initialValues.address ? initialValues.address : INITIAL_VALUES_ADDRESS,
      company_api_key: initialValues.company_api_key ? initialValues.company_api_key : INITIAL_VALUES_COMPANY_API_KEY
    },
    validationSchema: validationSchema,
    onSubmit: values => onSubmit(values),
  });

  const handleSetFieldValue = (field: string, value: any) => {
    if (editMode && field === 'supported_languages' && values.supported_languages.length < value.length) {
      dispatch({
        type: types.ADD_TOAST,
        payload: {
          type: 'info',
          message: LANGUAGE_INFO_MESSAGE
        }
      })
    }
    setFieldValue(field, value)
  }

  return (
    <Box mt={4}>
      <form onSubmit={handleSubmit}>
        <CompanyInformationSection
          values={values}
          touched={touched}
          errors={errors}
          handleChange={handleChange}
          typeOptions={typeOptions}
          employeesOptions={employeesOptions}
          setFieldValue={setFieldValue}
        />
        <Box mt={4}>
          <CompanyAddressSection
            handleChange={handleChange}
            values={values}
            setValues={setValues}
            initialValues={initialValues}
            errors={errors}
          />
        </Box>
        <Box mt={4}>
          <CompanySupportsSection
            values={values}
            setFieldValue={handleSetFieldValue}
            a11yStandardsOptions={a11yStandardsOptions}
            languagesOptions={languagesOptions}
            errors={errors}
            touched={touched}
          />
        </Box>
        <Box mt={4}>
          <CompanyAPIKeySection
            values={values}
            setFieldValue={setFieldValue}
            handleChange={handleChange}
            errors={errors}
            touched={touched}
          />
        </Box>
        {/*<Box mt={4}>*/}
          {/*<CompanyContractsSection*/}
          {/*  values={values}*/}
          {/*  setFieldValue={setFieldValue}*/}
          {/*  handleChange={handleChange}*/}
          {/*  errors={errors}*/}
          {/*  touched={touched}*/}
          {/*  invoiceStatusOptions={invoiceStatusOptions}*/}
          {/*  products={products}*/}
          {/*/>*/}
        {/*</Box>*/}
        {/*<Box mt={4}>*/}
        {/*  <CompanyButtonSettingsSection/>*/}
        {/*</Box>*/}
        <Box mt={4}>
          <Card>
            <CardContent>
              <Box display={'flex'} justifyContent={'center'} alignItems={'center'}>
                <GradientButton
                  type="submit"
                  style={{minWidth: '100px'}}
                  disabled={loading}
                >
                  Save
                  {loading ? (
                    <Box ml={1} display={'flex'} justifyContent={'center'} alignItems={'center'}>
                      <CircularProgress size={15}/>
                    </Box>
                  ) : null}
                </GradientButton>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </form>
    </Box>
  )
}

export default CompanyForm